import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { tx, UI } from '../lib/i18n.ts';
import { loginHref } from '../lib/guest.ts';
import { useApp } from '../state/app.tsx';
import { Icon } from './Icon.tsx';

/**
 * 游客点了「保存」「加进来」这一类按钮时弹出来的那一个框。
 *
 * ════════════════════════════════════════════════════════
 * 为什么是一个框，而不是直接跳去登录页
 * ════════════════════════════════════════════════════════
 * 直接跳走会把他正在看的东西从眼前拿走 —— 而他点保存的那一刻，
 * 多半只是想看看「保存之后会怎样」，并没有决定要注册。
 * 先说清楚两件事再让他选：
 *   1. 为什么要登录（这一步要存进账号）、要付出什么（一个邮箱，不要密码不要签名）
 *   2. 他刚才改的东西不会丢（见 guest.ts 的草稿暂存）
 * 选「先不了」就原样留在这一页，什么都没变。
 *
 * 全站只有这一个框（放在根上），各页面通过 `useGuestPrompt()` 叫它。
 * 每页自己画一个的话，文案和行为一定会慢慢不一样。
 */

export interface GuestPromptOptions {
  /** 他刚才改了几项、我们替他留着了。>0 时框里会多一句「改动还在」 */
  kept?: number | undefined;
}

type Ask = (opts?: GuestPromptOptions) => void;

const Ctx = createContext<Ask>(() => undefined);

/** 叫出「登录后才能保存」那个框 */
export const useGuestPrompt = (): Ask => useContext(Ctx);

export function GuestPromptHost({ children }: { children: React.ReactNode }): React.ReactElement {
  const app = useApp();
  const lang = app.lang;
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState<GuestPromptOptions | null>(null);
  const primary = useRef<HTMLButtonElement | null>(null);
  /* 关掉之后把焦点还给点开它的那个按钮 —— 用键盘的人不该被扔回页面顶上 */
  const opener = useRef<HTMLElement | null>(null);

  const ask = useCallback<Ask>(opts => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setOpen(opts ?? {});
  }, []);

  const close = useCallback(() => {
    setOpen(null);
    const el = opener.current;
    opener.current = null;
    if (el) setTimeout(() => el.focus(), 0);
  }, []);

  useEffect(() => {
    if (open === null) return;
    primary.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const goLogin = (): void => {
    setOpen(null);
    opener.current = null;
    navigate(loginHref(loc.pathname + loc.search));
  };

  const kept = open?.kept ?? 0;

  return (
    <Ctx.Provider value={ask}>
      {children}
      {open !== null ? (
        <div
          className="modal-back"
          onClick={e => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="modal narrow" role="dialog" aria-modal="true" aria-labelledby="guest-prompt-t">
            <div className="modal-h">
              <Icon name="lock" />
              <b id="guest-prompt-t">{tx(UI.promptTitle, lang)}</b>
              <span style={{ marginLeft: 'auto' }} />
              <button type="button" className="iconbtn" aria-label={tx(UI.close, lang)} onClick={close}>
                <Icon name="x" cls="sm" />
              </button>
            </div>
            <div className="modal-b" style={{ display: 'grid', gap: 12, lineHeight: 1.7 }}>
              <div>{tx(UI.promptBody, lang)}</div>
              {kept > 0 ? (
                <div className="banner ok">
                  <span className="ic"><Icon name="done" /></span>
                  <span>{tx(UI.promptKept, lang).replace('{n}', String(kept))}</span>
                </div>
              ) : null}
              <div className="hint">{tx(UI.noWalletSign, lang)}</div>
            </div>
            <div className="modal-f">
              <button type="button" className="btn" onClick={close}>{tx(UI.promptLater, lang)}</button>
              <button ref={primary} type="button" className="btn primary" onClick={goLogin}>
                {tx(UI.guestCta, lang)}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Ctx.Provider>
  );
}

/**
 * 游客模式下的示例数据提示。放在有示例数据的那几页最上面。
 *
 * 示例数据在这个产品里必须标得**一眼就能看出来** ——
 * 风控工具的界面上出现一个看着像真的盈亏数字，
 * 比出现一片空白危险（见 NotBuilt.tsx 开头那段）。
 */
export function SampleNotice({ what }: { what: string }): React.ReactElement {
  const app = useApp();
  return (
    <div className="banner info sample-notice" role="note">
      <span className="ic"><Icon name="eye" /></span>
      <span>
        <b>{tx(UI.sampleData, app.lang)}</b>
        <span className="tag accent" style={{ marginLeft: 8 }}>{tx(UI.sample, app.lang)}</span>
        <div className="hint" style={{ marginTop: 4 }}>{what}</div>
      </span>
      {/* 这里刻意**不放**注册按钮：正上方的游客条和顶栏各有一个，
          一屏三个一样的按钮只会让人觉得在被推销 */}
    </div>
  );
}
